import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import AdminKeyGate from '../components/AdminKeyGate';
import { setAdminKey, getRaffleDisplay, drawAdminRaffle } from '../api';
import { raffleEntryUrl, qrSvg } from '../raffle/qr';
import './RaffleWallPage.css';

// Preview uses only synthetic fixtures, requires the local key gate like the live wall,
// and never issues a network request (even when the fixture state changes).
export function previewRaffle(mode = 'open') {
  const drawn = ['reveal', 'winner', 'long', 'network'].includes(mode);
  const name = mode === 'long' ? 'Alexandra-Christina von Example-Andersson Very Long Sample Name' : 'Alex Example';
  const revealAt = mode === 'reveal' ? Date.now() + 2300 : Date.now() - 120000;
  return {
    id: 1, title: 'DEMO · Ångkvarn raffle night', prize: 'A golden demonstration prize',
    state: drawn ? 'drawn' : (mode === 'closed' ? 'closed' : mode === 'draft' ? 'draft' : 'open'),
    entry_count: 42, server_time: new Date().toISOString(),
    draw: drawn ? { winner_name: name, eligible_count: 42,
      drawn_at: new Date(revealAt - 1500).toISOString(), reveal_at: new Date(revealAt).toISOString() } : null,
  };
}

const badAuth = e => [401,403].includes(e?.response?.status);
const deleted = e => e?.response?.status === 404;
const isAbort = e => e?.code === 'ERR_CANCELED' || e?.name === 'AbortError';
// TV layout keeps the winner's full name; only secondary event metadata is
// display-limited. The server, admin page and guest page retain full values.
const podiumMeta = (value, limit) => {
  const chars = Array.from(String(value || ''));
  return chars.length > limit ? `${chars.slice(0, limit - 1).join('')}…` : chars.join('');
};

function restoreKey() {
  const stored = sessionStorage.getItem('f1tips_adminkey') || '';
  setAdminKey(stored);
  return stored;
}

export default function RaffleWallPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const mode = id === 'preview' ? (params.get('state') || 'open') : null;
  const [key, setKey] = useState(restoreKey);
  const [denied, setDenied] = useState(false);
  const revoke = useCallback(() => {
    sessionStorage.removeItem('f1tips_adminkey'); setAdminKey(null); setDenied(true); setKey('');
  }, []);
  return !key ? <div className="raffle-wall-access">{denied && <p role="alert">Raffle display access expired. Enter the admin key again.</p>}<AdminKeyGate onKey={newKey=>{setDenied(false);setKey(newKey);}} /></div>
    : <RaffleWall key={`${id}:${mode || 'live'}:${key}`} id={id} mode={mode} onRevoked={revoke} />;
}

export function RaffleWall({ id, mode, onRevoked }) {
  const preview = id === 'preview';
  const [snapshot, setSnapshot] = useState(() => preview ? previewRaffle(mode) : null);
  const [status, setStatus] = useState(preview && mode === 'network' ? 'Connection interrupted · showing last confirmed result' : 'Connecting…');
  const [missing, setMissing] = useState(false);
  const [pendingDraw, setPendingDraw] = useState(false);
  const [hostError, setHostError] = useState('');
  const [serverOffset, setServerOffset] = useState(0);
  const [nowTick, setNowTick] = useState(Date.now());
  const [motion, setMotion] = useState(false);
  const seenDraw = useRef(null);
  const lastConfirmed = useRef(null);
  const alive = useRef(false);
  const timer = useRef(null);
  const inFlight = useRef(null);
  const drawAbort = useRef(null);
  const generation = useRef(0);
  const backoff = useRef(5000);
  const drawLock = useRef(false);
  const pollNow = useRef(null);

  // GET display alone: no winner contact email ever enters the wall state.
  const apply = useCallback(payload => {
    // A draw is permanent: a delayed/cached pre-draw response cannot roll it back.
    // A confirmed 403/404 is handled separately and still revokes/removes the wall.
    if (lastConfirmed.current?.draw && !payload.draw) {
      setStatus('Verifying saved winner · waiting for updated server state');
      return;
    }
    const draw = payload.draw;
    const offset = Date.parse(payload.server_time) - Date.now();
    setServerOffset(Number.isFinite(offset) ? offset : 0);
    const identity = draw && `${draw.drawn_at}|${draw.reveal_at}`;
    if (identity && identity !== seenDraw.current) {
      // Animate only while the persisted reveal window is current; historical
      // winners on refresh render statically, never replay an intro.
      const remaining = Date.parse(draw.reveal_at) - (Date.now() + (Number.isFinite(offset) ? offset : 0));
      setMotion(remaining > -1000 && remaining < 3500);
      seenDraw.current = identity;
    }
    lastConfirmed.current = payload;
    setSnapshot(payload);
    setNowTick(Date.now());
    setStatus('Connected'); setMissing(false);
  }, []);

  useEffect(() => {
    alive.current = true;
    if (preview) return () => { alive.current = false; };
    let cancelled = false;
    let terminal = false;
    const schedule = (delay, callback) => {
      clearTimeout(timer.current);
      if (!cancelled) timer.current = setTimeout(callback, delay);
    };
    const poll = async () => {
      if (cancelled || terminal || inFlight.current) return;
      if (drawLock.current) { schedule(1000, poll); return; }
      const controller = new AbortController();
      const ticket = ++generation.current;
      inFlight.current = controller;
      try {
        const response = await getRaffleDisplay(id, { signal: controller.signal });
        if (cancelled || controller.signal.aborted || generation.current !== ticket) return;
        apply(response);
        backoff.current = 5000;
      } catch (e) {
        if (cancelled || controller.signal.aborted || generation.current !== ticket || isAbort(e)) return;
        if (badAuth(e)) { terminal=true;lastConfirmed.current=null;setSnapshot(null);return onRevoked(); }
        if (deleted(e)) { terminal=true;lastConfirmed.current=null;setSnapshot(null);setMissing(true);setStatus('Raffle removed');return; }
        setStatus(lastConfirmed.current ? 'Connection interrupted · showing last confirmed result' : 'Connection unavailable · retrying');
        backoff.current = Math.min(30000, Math.max(5000, backoff.current * 2));
      } finally {
        if (inFlight.current === controller) {
          inFlight.current = null;
          if (!cancelled && !terminal && alive.current) {
            schedule(document.hidden ? 30000 : backoff.current, poll);
          }
        }
      }
    };
    const visibility = () => {
      if (document.hidden) {
        // Cancel stale fetch and use a slow check when hidden, not rapid TV polling.
        if (inFlight.current) {
          generation.current += 1;
          inFlight.current.abort();
          inFlight.current = null;
        }
        clearTimeout(timer.current);
        schedule(30000, poll);
      } else {
        clearTimeout(timer.current);
        // A cancelled request can settle after visibilitychange; schedule so
        // the next read starts after the old request's finally executes.
        schedule(100, poll);
      }
    };
    pollNow.current = poll;
    document.addEventListener('visibilitychange', visibility);
    poll();
    return () => {
      cancelled = true;alive.current = false;generation.current += 1;
      pollNow.current = null;
      clearTimeout(timer.current);inFlight.current?.abort();inFlight.current = null;
      drawAbort.current?.abort();drawAbort.current = null;
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [id, preview, apply, onRevoked]); // wall component is key-remounted for each raffle/preview state

  // One reveal timer, derived from persisted server timestamps; no animation loop.
  const revealTime = snapshot?.draw?.reveal_at;
  useEffect(() => {
    if (!revealTime) return;
    const ms = Date.parse(revealTime) - (Date.now() + serverOffset);
    if (ms <= 0) return;
    const timer = setTimeout(() => setNowTick(Date.now()), ms + 25);
    return () => clearTimeout(timer);
  }, [revealTime, serverOffset]);

  const draw = async () => {
    if (drawLock.current || pendingDraw || !snapshot || snapshot.state !== 'closed' || !snapshot.entry_count || preview) return;
    if (!window.confirm('Draw the one permanent winner? No reroll is possible.')) return;
    drawLock.current = true;
    // Mutations and display GETs share one generation. Invalidate and abort any
    // prior poll *before* sending POST; late poll completions cannot hide a draw.
    generation.current += 1;
    inFlight.current?.abort();
    inFlight.current = null;
    clearTimeout(timer.current);
    const ticket = ++generation.current;
    const controller = new AbortController();
    drawAbort.current = controller;
    setPendingDraw(true); setHostError('');
    try {
      await drawAdminRaffle(id, { signal: controller.signal });
      // POST response deliberately not rendered; authoritative display excludes email.
      const current = await getRaffleDisplay(id, { signal: controller.signal });
      if (alive.current && !controller.signal.aborted && generation.current === ticket) apply(current);
    } catch (e) {
      if (!alive.current || controller.signal.aborted || generation.current !== ticket) return;
      if (badAuth(e)) return onRevoked();
      if (deleted(e)) { lastConfirmed.current=null;setSnapshot(null);setMissing(true);setStatus('Raffle removed');return; }
      setHostError('Draw status uncertain. Reloading the saved result; do not retry until verified.');
      try {
        const current = await getRaffleDisplay(id, { signal: controller.signal });
        if (alive.current && !controller.signal.aborted && generation.current === ticket) apply(current);
      } catch (recoveryError) {
        if (alive.current && !controller.signal.aborted && generation.current === ticket) {
          if (badAuth(recoveryError)) return onRevoked();
          if (deleted(recoveryError)) {
            lastConfirmed.current=null;setSnapshot(null);setMissing(true);setStatus('Raffle removed');
          }
        }
      }
    } finally {
      if (alive.current && !controller.signal.aborted) setPendingDraw(false);
      drawLock.current = false;
      if (drawAbort.current === controller) drawAbort.current = null;
      // The cancelled poll cannot schedule its successor. Resume the shared
      // poll loop after a draw/recovery attempt (including uncertain POSTs).
      if (alive.current && !controller.signal.aborted && generation.current === ticket && pollNow.current) {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => pollNow.current?.(), document.hidden ? 30000 : 5000);
      }
    }
  };

  const canShowWinner = !!snapshot?.draw &&
    nowTick + serverOffset >= Date.parse(snapshot.draw.reveal_at);
  const qrUrl = snapshot ? (preview ? new URL('/raffles/preview', window.location.origin).href : raffleEntryUrl(id)) : '';
  // qrSvg only processes the URL; data from the database is not interpolated into HTML.
  const qr = useMemo(() => {
    try { return qrUrl ? qrSvg(qrUrl) : ''; }
    catch (_) { return ''; }
  }, [qrUrl]);
  const state = snapshot?.state;
  return <main className={`raffle-wall raffle-wall-${canShowWinner ? 'winner' : state || 'loading'}`}>
    <div className="raffle-wall-brand"><img src="/brygg_angkvarn_vit_no_text.png" alt="Ångkvarn" /><div><span>BRYGGERIET ÅNGKVARN</span><strong>EVENT RAFFLE</strong></div></div>
    <div className="raffle-wall-connection" role="status" aria-live="polite">{preview ? `SYNTHETIC PREVIEW · ${mode}${mode === 'network' ? ' · connection interrupted' : ''}` : status}</div>
    {missing ? <section className="raffle-wall-message"><h1>Raffle unavailable</h1><p>This raffle was removed. Ask the organiser for a new link.</p></section>
      : !snapshot ? <section className="raffle-wall-message"><h1>Connecting to raffle…</h1><p>Waiting for the organiser’s server.</p></section>
      : canShowWinner ? <section className={`raffle-wall-podium ${motion ? 'raffle-wall-arrival' : ''}`} aria-label="Raffle winner" aria-live="polite">
          <div className="raffle-wall-crown">✦ &nbsp; THE WINNER &nbsp; ✦</div>
          <div className="raffle-wall-medal">1</div>
          <h1 className={Array.from(snapshot.draw.winner_name || '').length > 70 ? 'raffle-wall-name-max' :
            Array.from(snapshot.draw.winner_name || '').length > 40 ? 'raffle-wall-name-long' : ''}>{snapshot.draw.winner_name}</h1>
          <p className="raffle-wall-congrats">CONGRATULATIONS</p>
          <div className="raffle-wall-gold-platform"><span>1ST PLACE</span></div>
          <p className="raffle-wall-prize" title={snapshot.prize || 'One lucky winner'}>{podiumMeta(snapshot.prize || 'One lucky winner', 96)}</p>
          <p className="raffle-wall-footnote" title={snapshot.title}>{podiumMeta(snapshot.title, 80)} · {snapshot.draw.eligible_count} eligible entries</p>
        </section>
      : <section className="raffle-wall-main">
        <div className="raffle-wall-info">
          <p className="raffle-wall-overline">{state==='open'?'ENTRIES ARE OPEN':state==='closed'?'ENTRIES ARE CLOSED':state==='drawn'?'WINNER REVEAL': 'COMING SOON'}</p>
          <h1>{snapshot.title}</h1><div className="raffle-wall-line" />
          <p className="raffle-wall-prize-label">THE PRIZE</p><h2>{snapshot.prize || 'One prize · One winner'}</h2>
          <p className="raffle-wall-count"><strong>{snapshot.entry_count}</strong> {snapshot.entry_count===1?'ENTRY':'ENTRIES'}</p>
          {state==='draft' && <p>Entries have not opened yet. Follow the organiser for updates.</p>}
          {state==='closed' && <p>Entries are closed. Waiting for the host to draw the winner.</p>}
          {state==='drawn' && <p>The draw is saved. Get ready for the reveal…</p>}
        </div>
        {state==='open' && <aside className="raffle-wall-scan"><div className="raffle-wall-qr">{qr ? <div dangerouslySetInnerHTML={{ __html: qr }} /> : <p role="status">QR unavailable. Type the URL below.</p>}</div><strong>{preview ? 'DEMO QR · NOT LIVE' : 'SCAN TO ENTER'}</strong><p>Enter your name and email · One chance per email</p><div className="raffle-wall-url">{qrUrl}</div></aside>}
        {state==='closed' && snapshot.entry_count>0 && !preview && <aside className="raffle-wall-host"><p>Host controls</p><button disabled={pendingDraw} onClick={draw}>{pendingDraw?'Checking saved draw…':'Draw winner'}</button><small>Requires confirmation · One permanent result</small></aside>}
        {hostError && <p role="alert" className="raffle-wall-host-error">{hostError}</p>}
      </section>}
    <div className="raffle-wall-bottom">ONE CHANCE · ONE WINNER · NO REROLL</div>
  </main>;
}
